import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import {
  ADMIN_ALL_TRANSPORT_QUERY,
  CREATE_TRANSPORT_VEHICLE_MUTATION,
  UPDATE_TRANSPORT_VEHICLE_MUTATION,
  DELETE_TRANSPORT_VEHICLE_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';
import { useFamily } from '../context/FamilyContext';
import { ownerName, actAsOwner } from '../utils/familyOwner';

const STATUS_COLOR = { available: 'success', in_use: 'warning', maintenance: 'error' };
const VEHICLE_TYPES = ['car','van','minibus','bus','auto','tempo_traveller','luxury_car','other'];

const EMPTY = { vehicleNumber: '', vehicleType: 'car', capacity: 4, driverName: '', driverPhone: '', notes: '' };

export default function AdminTransportationPage() {
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [snack, setSnack]     = useState({ open: false, msg: '', ok: true });

  const { family } = useFamily();
  const { data, loading, refetch } = useQuery(ADMIN_ALL_TRANSPORT_QUERY, { fetchPolicy: 'cache-and-network' });

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [create, { loading: creating }] = useMutation(CREATE_TRANSPORT_VEHICLE_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Vehicle added.'); },
    onError: e => toast(e.message, false),
  });
  const [update, { loading: updating }] = useMutation(UPDATE_TRANSPORT_VEHICLE_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Vehicle updated.'); },
    onError: e => toast(e.message, false),
  });
  const [remove] = useMutation(DELETE_TRANSPORT_VEHICLE_MUTATION, {
    onCompleted: () => { refetch(); toast('Vehicle removed.'); },
    onError: e => toast(e.message, false),
  });

  const vehicles = data?.allTransportVehicles || [];

  const openEdit = (v) => {
    setEditing(v);
    setForm({ vehicleNumber: v.vehicleNumber, vehicleType: v.vehicleType, capacity: v.capacity, driverName: v.driverName || '', driverPhone: v.driverPhone || '', notes: v.notes || '' });
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY); };

  const handleSave = () => {
    if (!form.vehicleNumber.trim()) return;
    const vars = { ...form, capacity: parseInt(form.capacity) || 4 };
    if (editing) update({ variables: { id: editing.id, ...vars }, ...actAsOwner(editing.owner) });
    else create({ variables: vars });
  };

  const totalCapacity = vehicles.filter(v => v.status === 'available').reduce((s, v) => s + v.capacity, 0);

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Transport Fleet</Typography>
          <Typography variant="body2" color="text.secondary">
            {family ? `${family.displayName}: ` : 'All bride/groom families: '}
            {vehicles.length} vehicles — {totalCapacity} total available seats
          </Typography>
        </Box>
        <Tooltip title={family ? '' : 'Select a bride/groom family above to add a vehicle for them'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!family} onClick={() => { setEditing(null); setForm(EMPTY); setDialog(true); }}>
              Add Vehicle
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <ContractedVendorsSection category="transportation" />

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      {/* Summary by type */}
      {vehicles.length > 0 && (
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
          {[...new Set(vehicles.map(v => v.vehicleType))].map(type => (
            <Chip key={type} icon={<DirectionsBusIcon />} label={`${type}: ${vehicles.filter(v => v.vehicleType === type).length}`} size="small" variant="outlined" />
          ))}
        </Stack>
      )}

      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {!family && <TableCell>Family</TableCell>}
              <TableCell>Vehicle No.</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Driver Phone</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.map(v => (
              <TableRow key={v.id} hover>
                {!family && <TableCell>{ownerName(v.owner)}</TableCell>}
                <TableCell><strong>{v.vehicleNumber}</strong></TableCell>
                <TableCell>{v.vehicleType}</TableCell>
                <TableCell>{v.capacity}</TableCell>
                <TableCell>{v.driverName || '—'}</TableCell>
                <TableCell>{v.driverPhone || '—'}</TableCell>
                <TableCell>{v.vendor?.name || '—'}</TableCell>
                <TableCell><Chip label={v.status} size="small" color={STATUS_COLOR[v.status] || 'default'} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: v.id }, ...actAsOwner(v.owner) })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {vehicles.length === 0 && (
              <TableRow><TableCell colSpan={family ? 8 : 9} align="center">{family ? `No vehicles for ${family.displayName} yet.` : 'No vehicles in fleet yet.'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? `Edit Vehicle — ${ownerName(editing.owner)}` : `Add Vehicle for ${family?.displayName || ''}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Vehicle Number" value={form.vehicleNumber} onChange={e => setForm(f => ({ ...f, vehicleNumber: e.target.value }))} required fullWidth />
            <TextField select label="Type" value={form.vehicleType} onChange={e => setForm(f => ({ ...f, vehicleType: e.target.value }))} fullWidth>
              {VEHICLE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Capacity (persons)" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} type="number" fullWidth inputProps={{ min: 1 }} />
            <TextField label="Driver Name" value={form.driverName} onChange={e => setForm(f => ({ ...f, driverName: e.target.value }))} fullWidth />
            <TextField label="Driver Phone" value={form.driverPhone} onChange={e => setForm(f => ({ ...f, driverPhone: e.target.value }))} fullWidth />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} startIcon={<SaveIcon />}>
            {editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
